import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaUsers } from "react-icons/fa";
import { apihubGet } from "../../api/apihub";
import {
  createAgent,
  fetchProjectForTeams,
} from "../../api/teams.api";
import { businessPath } from "../../config/businessMenu";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const COUNTRIES = [
  { code: "+91", name: "India", iso: "IN" },
  { code: "+1", name: "United States", iso: "US" },
  { code: "+44", name: "United Kingdom", iso: "GB" },
  { code: "+971", name: "United Arab Emirates", iso: "AE" },
  { code: "+65", name: "Singapore", iso: "SG" },
  { code: "+61", name: "Australia", iso: "AU" },
  { code: "+49", name: "Germany", iso: "DE" },
  { code: "+33", name: "France", iso: "FR" },
  { code: "+81", name: "Japan", iso: "JP" },
  { code: "+82", name: "South Korea", iso: "KR" },
];

const EMPTY_FORM = {
  fullName: "",
  email: "",
  mobile: "",
  password: "",
  address: "",
  teamRole: null,
};

const CreateTeams = () => {
  const { projectId } = useProjectPhone();
  const [form, setForm] = useState(EMPTY_FORM);
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [teamCount, setTeamCount] = useState(0);
  const [maxAgents, setMaxAgents] = useState(null);
  const [adminId, setAdminId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [projectRes, profileRes] = await Promise.all([
        fetchProjectForTeams(projectId),
        apihubGet("/getInteractedCustomerDetail/profile").catch(() => null),
      ]);
      const project = projectRes?.project || projectRes?.data?.project || {};
      setTeamRoles(project.teamroles || []);
      setTeamCount(Array.isArray(project.teams) ? project.teams.length : 0);
      setMaxAgents(
        typeof project.noOfAgents === "number" ? project.noOfAgents : null,
      );
      const user = profileRes?.user || profileRes;
      const matched = user?.matchedUser || user;
      if (matched?._id) setAdminId(matched._id);
    } catch (err) {
      setError(err.message || "Failed to load team settings");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setMessage("");
  };

  const onRoleChange = (e) => {
    const role = teamRoles.find((r) => r._id === e.target.value) || null;
    setForm((prev) => ({ ...prev, teamRole: role }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!projectId) return;
    if (!country?.code) {
      setError("Please select a country code.");
      return;
    }
    if (!form.fullName.trim() || !form.email.trim()) {
      setError("Please enter full name and email.");
      return;
    }
    if (!form.teamRole) {
      setError("Please select a team role.");
      return;
    }
    if (maxAgents != null && teamCount >= maxAgents) {
      setError("Please upgrade your plan to add more agents.");
      return;
    }

    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const res = await createAgent({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        mobile: `${country.code}${form.mobile.trim()}`,
        password: form.password,
        extractedalphaid: adminId,
        extractedalphaprojectid: projectId,
        address: form.address,
        teamRole: form.teamRole,
      });
      if (res?.success === false) {
        throw new Error(res.message || "Failed to create agent");
      }
      setMessage(res?.message || "Team created successfully!");
      setForm(EMPTY_FORM);
      setTeamCount((c) => c + 1);
    } catch (err) {
      setError(err.message || "Failed to create agent");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="teams-page">
        <p className="teams-loading">Loading…</p>
      </div>
    );
  }

  return (
    <div className="teams-page">
      <header className="teams-header">
        <h1>
          <FaUsers aria-hidden />
          All Agents
        </h1>
        <p>
          Distribute conversations across agents to ensure quicker responses and
          better customer experience.
        </p>
      </header>

      <section className="teams-form-card">
        <div className="teams-form-card-head">
          <h2>Create Agent</h2>
          <Link to={businessPath(projectId, "allteams")} className="teams-back-btn">
            <FaArrowLeft aria-hidden />
            Go Back
          </Link>
        </div>

        {error && <p className="teams-alert teams-alert-error">{error}</p>}
        {message && <p className="teams-alert teams-alert-success">{message}</p>}

        {maxAgents != null && (
          <p className="teams-limit-hint">
            Agents used: {teamCount} / {maxAgents}
          </p>
        )}

        <form className="teams-form" onSubmit={onSubmit}>
          <div className="teams-form-grid teams-form-grid-3">
            <label className="teams-field">
              <span>Full Name *</span>
              <input
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={onChange}
                placeholder="Enter full name"
                required
              />
            </label>
            <label className="teams-field">
              <span>Email *</span>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="Enter email"
                required
              />
            </label>
            <label className="teams-field">
              <span>Mobile Number *</span>
              <div className="teams-mobile-row">
                <select
                  value={country.iso}
                  onChange={(e) => {
                    const next = COUNTRIES.find((c) => c.iso === e.target.value);
                    if (next) setCountry(next);
                  }}
                  aria-label="Country code"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.iso}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="mobile"
                  value={form.mobile}
                  onChange={onChange}
                  placeholder="Enter mobile number"
                  required
                />
              </div>
            </label>
          </div>

          <label className="teams-field">
            <span>Password *</span>
            <div className="teams-password-wrap">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={onChange}
                placeholder="Enter password"
                required
              />
              <button
                type="button"
                className="teams-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </label>

          <label className="teams-field">
            <span>Address</span>
            <textarea
              name="address"
              value={form.address}
              onChange={onChange}
              placeholder="Enter address"
              rows={3}
            />
          </label>

          <label className="teams-field teams-field-half">
            <span>Team Role *</span>
            <select
              value={form.teamRole?._id || ""}
              onChange={onRoleChange}
              required
            >
              <option value="">Select</option>
              {teamRoles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>
          </label>

          <button type="submit" className="teams-submit-btn" disabled={submitting}>
            {submitting ? "Creating…" : "Create"}
          </button>
        </form>
      </section>
    </div>
  );
};

export default CreateTeams;
